import { useCallback, useMemo } from 'react';
import { fetchProjects, fetchResolutions, fetchSprints, fetchTickets } from '../../api/endpoints';
import type {
  Id,
  Page,
  Project,
  ResolutionDef,
  Sprint,
  TicketListItem,
  TicketSort,
  TicketSortDir,
} from '../../api/types';
import { FilterBar, describeFilters } from '../../components/backlog/FilterBar';
import { Pagination } from '../../components/backlog/Pagination';
import { TicketDetailPanel } from '../../components/detail/TicketDetailPanel';
import { EmptyState } from '../../components/ui/EmptyState';
import { ErrorNotice, StaleBanner } from '../../components/ui/Notice';
import { ListSkeleton } from '../../components/ui/Skeleton';
import { useResource } from '../../hooks/useResource';
import { useBacklogParams } from '../../pages/useBacklogParams';
import { useVocabulary } from '../../vocab/VocabularyProvider';
import { ListTable } from './ListTable';
import { buildListQuery, SORT_OPTIONS } from './list-params';
import { useListParams } from './useListParams';

/**
 * 🔴 THE LIST VIEW — Or's shot 7. A dense table for FINDING a ticket.
 *
 * ⭐ THE DISTINCTION FROM THE BACKLOG IS THE POINT AND IS WORTH KEEPING. The backlog groups by sprint,
 * sums story points, nests sub-tasks under parents and orders by `backlog_rank`: it is for deciding
 * what happens next. This screen is flat, one row per ticket, every field in a fixed column, ordered
 * by the server and paged 50 at a time: it is for answering "where is that ticket". ⛔ Making either
 * one grow the other's features ends with two copies of the same screen and no reason to open the
 * second.
 *
 * ⚠️ IT DOES NOT RENDER THE TOP BAR — the shell owns `App.tsx` and `components/layout`.
 */

const NO_SPRINTS: Sprint[] = [];

export function ListPage() {
  const vocab = useVocabulary();
  const { filters, page, selectedId, setFilters, setPage, select } = useBacklogParams();
  const { sort, dir, effectiveDir, setSort, toggleSort } = useListParams();

  const projects = useResource<Project[]>((signal) => fetchProjects(signal), []);

  const sprints = useResource<Sprint[]>(
    (signal) => fetchSprints(filters.projectId as Id, signal),
    [filters.projectId],
    { enabled: Boolean(filters.projectId) },
  );

  /**
   * ⭐ The Resolution column's labels. Loaded on this screen only — it is the only screen with that
   * column. ⚠️ If it fails, rows show the raw code with a title saying so; they do not show a guess
   * and they do not show a blank, which would read as "no resolution".
   */
  const resolutions = useResource<ResolutionDef[]>((signal) => fetchResolutions(signal), []);

  const resolutionLabel = useCallback(
    (code: string | null): string | null => {
      if (!code) return null;
      return resolutions.data?.find((entry) => entry.code === code)?.label ?? null;
    },
    [resolutions.data],
  );

  /**
   * 🔴 THE SORT IS IN THE REQUEST. `sort` is a dependency of this resource, so changing it re-fetches
   * from the server; the rows are then rendered in exactly the order they arrived. ⛔ Nothing here
   * re-sorts `tickets.data.items` — a client-side sort of the 50 rows on this page would disagree
   * with page 2, and the disagreement would be invisible.
   */
  const tickets = useResource<Page<TicketListItem>>(
    (signal) => fetchTickets(buildListQuery({ filters, page, sort, dir }), signal),
    // ⚠️ `dir` IS A DEPENDENCY. Reversing the order changes which rows this page holds, so leaving it
    //    out would leave the table showing the previous direction's rows under the new arrow.
    [filters.projectId, filters.status, filters.assignee, filters.sprintId, page, sort, dir],
  );

  const items = tickets.data?.items ?? [];

  const assigneeSuggestions = useMemo(
    () => [...new Set(items.map((item) => item.assignee).filter((name): name is string => !!name))].sort(),
    [items],
  );

  const statusLabel = filters.status ? (vocab.status(filters.status)?.label ?? filters.status) : null;
  const filterSummary = describeFilters(filters, projects.data ?? [], statusLabel);
  const hasFilters = Boolean(
    filters.projectId || filters.status || filters.assignee || filters.sprintId,
  );

  const listClass = selectedId
    ? 'lg:grid lg:grid-cols-[minmax(0,1fr)_26rem] xl:grid-cols-[minmax(0,1fr)_30rem]'
    : '';

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className={`min-h-0 flex-1 ${listClass}`}>
        <section
          aria-label="Ticket list"
          className="flex h-full min-h-0 flex-col bg-[color:var(--color-surface)]"
        >
          <FilterBar
            projects={projects.data ?? []}
            statuses={vocab.statuses}
            assigneeSuggestions={assigneeSuggestions}
            sprints={sprints.data ?? NO_SPRINTS}
            sprintsUnavailable={Boolean(sprints.error)}
            filters={filters}
            onChange={setFilters}
          />

          <SortBar sort={sort} dir={effectiveDir} onSort={setSort} onToggleDir={toggleSort} />

          <div className="space-y-2 px-3 pt-2 empty:hidden sm:px-4">
            {projects.error ? <ErrorNotice error={projects.error} onRetry={projects.reload} /> : null}
            {tickets.isStale && tickets.error ? (
              <StaleBanner error={tickets.error} loadedAt={tickets.loadedAt} onRetry={tickets.reload} />
            ) : null}
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto">
            {tickets.isLoading ? <ListSkeleton /> : null}

            {tickets.error && !tickets.isStale ? (
              <div className="p-3 sm:p-4">
                <ErrorNotice error={tickets.error} onRetry={tickets.reload} />
              </div>
            ) : null}

            {tickets.data && items.length === 0 && !tickets.error ? (
              <EmptyState
                title={hasFilters ? 'No tickets match this filter' : 'There are no tickets yet'}
                detail={
                  hasFilters
                    ? `Nothing matched ${filterSummary}. The request succeeded — this is an empty result, not a failure.`
                    : 'Once tickets exist in PlanHub they will appear here.'
                }
              />
            ) : null}

            {items.length > 0 ? (
              <ListTable
                items={items}
                sort={sort}
                dir={effectiveDir}
                onSort={toggleSort}
                selectedId={selectedId}
                onSelect={select}
                resolutionLabel={resolutionLabel}
                dimmed={tickets.isStale}
              />
            ) : null}
          </div>

          {tickets.data ? (
            <Pagination
              page={tickets.data.page}
              perPage={tickets.data.per_page}
              total={tickets.data.total}
              busy={tickets.isRefreshing}
              /* ⚠️ No "shown under their parents" note here, unlike the backlog: this table nests
                 nothing, so the range counts exactly the rows on screen. */
              onPageChange={setPage}
            />
          ) : null}
        </section>

        {selectedId ? (
          <div className="fixed inset-0 z-40 lg:static lg:z-auto lg:h-full lg:min-h-0">
            <TicketDetailPanel
              ticketId={selectedId}
              onClose={() => select(null)}
              onOpenTicket={(id) => select(id)}
              onStatusChanged={() => tickets.reload()}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}

/**
 * ⭐ The ordering control. Still here, and still naming what it does, now that most headers are live.
 *
 * ⚠️ IT IS NOT REDUNDANT WITH THE CLICKABLE HEADERS. `points` and `rank` have no column of their own,
 * so they are reachable only from here — and on the phone the table collapses to cards with no
 * headers at all, which is the layout Or reviews on. ⛔ Removing this control would make two
 * orderings unreachable on one breakpoint and all of them unreachable on the other.
 *
 * ⭐ The direction is a SEPARATE button rather than more `<option>`s: fourteen entries spelling out
 * every column-and-direction pair is a list nobody reads, and it would let a reader pick a
 * combination without noticing the column changed too.
 */
function SortBar({
  sort,
  dir,
  onSort,
  onToggleDir,
}: {
  sort: TicketSort;
  dir: TicketSortDir;
  onSort: (sort: TicketSort) => void;
  onToggleDir: (sort: TicketSort) => void;
}) {
  const active = SORT_OPTIONS.find((option) => option.value === sort);
  const ascending = dir === 'asc';

  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 border-b border-[color:var(--color-line)] px-3 py-2 sm:px-4">
      <label className="flex items-center gap-1.5">
        <span className="text-xs font-semibold text-[color:var(--color-ink-muted)]">Order</span>
        <select
          value={sort}
          onChange={(event) => onSort(event.target.value as TicketSort)}
          className="h-9 rounded border border-[color:var(--color-line)] bg-[color:var(--color-surface)] px-2 text-sm text-[color:var(--color-ink)]"
        >
          {SORT_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </label>

      {/* ⚠️ The label says which direction is IN EFFECT, not which one the click would select — a
          button reading "Ascending" that produces a descending list is the ambiguity worth avoiding.
          `aria-label` carries the action for a screen reader. */}
      <button
        type="button"
        onClick={() => onToggleDir(sort)}
        aria-label={`Sorted ${ascending ? 'ascending' : 'descending'} — switch to ${ascending ? 'descending' : 'ascending'}`}
        className="flex h-9 items-center gap-1 rounded border border-[color:var(--color-line)] px-2 text-sm text-[color:var(--color-ink)]"
      >
        <span aria-hidden="true">{ascending ? '↑' : '↓'}</span>
        <span>{ascending ? 'Ascending' : 'Descending'}</span>
      </button>

      <p className="min-w-0 text-[11.5px] text-[color:var(--color-ink-subtle)]">
        {active?.detail} — ordered by the server, so page 2 follows the same rule as page 1.
      </p>
    </div>
  );
}

export default ListPage;
